import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { MJConfirmService } from '@memberjunction/ng-ui-components';
import { AGUAppLocation, AGUCrumbItem, AGUCrumbService, AGURenderService, IsModuleReentry } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchApiService } from '../../services/research-api.service';
import { PortalConfigService } from '../../services/portal-config.service';
import { MembershipService } from '../../services/membership.service';
import { CAREER_LOCK_COPY, StatePanelCopy } from '../shared/state-copy';
import {
    ResearchCareerMatches, ResearchCareerNarrative, ResearchCvReviewStatus, ResearchJobMatch, ResearchJobProfile, ResearchMemberCv, ResearchPortalConfig, ResearchRoleMatch,
} from '../../models/research.models';
import { DescribeError } from '../shared/describe-error';
import {
    AwaitingSubmit, CONSENT_REQUIRED_COPY, ParseOnlyCopy, ParseOnlyMessage, CV_SUBMIT_GRACE_MS, CareerExplorerView, GroupReviewTags, NotProcessedCopy, NotProcessedMessage, PendingCounts, READING_STEPS, ReadingStep,
    OpeningsState, ReviewCard, ReviewCategories, ReviewCategory, StatedCount, TopRoles, TrailFor,
} from './career-explorer.state';
import { CxStartMode } from './cx-start.component';

/** How long review actions settle before they are sent as one `ReviewTags` call per status (brief). */
export const REVIEW_DEBOUNCE_MS = 600;

/** The poll interval when portal config is unavailable (`research.cv.pollIntervalMs` default). */
const DEFAULT_CV_POLL_MS = 4000;

/** Consecutive failed polls before the "trouble checking" banner shows. */
const POLL_FAILURES_BEFORE_BANNER = 3;

/** The server accepts at most 20 role refs per `CareerJobProfiles` call. */
const MAX_PROFILE_ROLES = 20;

/** Openings shown beside a role (P6:532 `list.slice(0,6)`): one page of Job Finder results for the role's first search title. */
const OPENINGS_COUNT = 6;

const CAREER_EXPLORER_VIEWS: readonly CareerExplorerView[] = ['start', 'ready', 'paths', 'role', 'review'];
const IsCareerExplorerView = (v: string): v is CareerExplorerView => (CAREER_EXPLORER_VIEWS as readonly string[]).includes(v);

const EMPTY_REVIEW: { cards: ReviewCard[]; pending: Record<ReviewCategory, number> } = {
    cards: [], pending: { skill: 0, education: 0, license: 0, certification: 0, title: 0 },
};

const IsParsing = (cv: ResearchMemberCv): boolean => cv.parseState === 'submitting' || cv.parseState === 'processing';

/**
 * Career Explorer (P6, `P/06_career-explorer.html`): the shell every Career Explorer view sits in. It owns the chrome (crumb,
 * "Powered by Julius", How this works, kebab), the member's CV and matches, CV polling, consent-gated upload, and the start,
 * reading, not-processed, error and parse-only states. Replaces the Sep-1 Career Map page.
 *
 * Root component of the `career-map` module (the module key is kept; only its title is "Career Explorer"), rendered via
 * `ngComponentOutlet` with no inputs, so every dependency comes through constructor injection.
 */
@Component({
    standalone: false,
    selector: 'ra-career-explorer',
    templateUrl: './career-explorer-page.component.html',
    styleUrl: './career-explorer-page.component.scss',
    host: { 'data-accent': 'plum', class: 'ra-fullwidth' },
})
export class CareerExplorerPageComponent implements OnInit, OnDestroy {
    view: CareerExplorerView = 'start';
    roleRef: string | null = null;
    /** The review category from the location's `RecordID` (`review` view). */
    category: string | null = null;
    cv: ResearchMemberCv | null = null;
    matches: ResearchCareerMatches | null = null;
    profiles = new Map<string, ResearchJobProfile>();
    matchesInFlight = false;
    /** The AI narrative of the role on screen, kept while its `narrativeKey` matches; null while loading, failed, or not asked for. */
    narrative: ResearchCareerNarrative | null = null;
    narrativeLoading = false;
    /** The Job Finder listings of the role on screen; null until asked for (and after a failed ask). */
    openings: ResearchJobMatch[] | null = null;
    openingsState: OpeningsState = 'idle';
    private narrativeFor: string | null = null;
    private narrativeFailedFor: string | null = null;
    private openingsFor: string | null = null;
    private readonly profilesInFlight = new Set<string>();
    /** Profile refs whose read failed: not asked for again until the CV is re-opened, so a failure cannot loop. */
    private readonly profilesFailed = new Set<string>();
    /** The document the loaded `matches` belong to: a return to the same CV refreshes them quietly (H-13). */
    private matchesFor: string | null = null;
    /** Bumped per `CareerMatches` request: a response older than the latest one is dropped (I-2). */
    private matchesRequest = 0;
    /** Set when the provider re-read the CV and dropped earlier decisions. */
    reread: { dropped: number } | null = null;

    config: ResearchPortalConfig | null = null;
    loading = true;
    loadError: string | null = null;
    matchesError: string | null = null;
    uploading = false;
    uploadError: string | null = null;
    /** Three polls in a row failed: a non-blocking banner, the page stays (polling continues). */
    pollTroubled = false;
    consent = false;
    /** The member chose "Replace" / "Choose another file": show the drop even though a CV exists. */
    replacing = false;
    /**
     * The server reports the CV on screen being read again: the done CV this page held is back in `submitting`/`processing` (the
     * provider forgot it and a matches call re-read it). Never inferred from `providerExpiresAt`: an expired copy the provider still
     * remembers just answers (I-1).
     */
    rereading = false;
    AboutOpen = false;
    MenuOpen = false;
    /** The short-lived link the review viewer loads the member's PDF from; null until read (or when it could not be). */
    downloadUrl: string | null = null;
    private downloadFor: string | null = null;
    /** Decisions sent but not yet confirmed by a re-read: the card leaves the queue at the click, not 600 ms later. */
    private readonly decisions = new Map<string, ResearchCvReviewStatus>();
    /** Bumped whenever `decisions` changes: with the CV reference it keys the memoized review cards. */
    private decisionsVersion = 0;
    private cardsMemo: { cv: ResearchMemberCv; version: number; cards: ReviewCard[]; pending: Record<ReviewCategory, number> } | null = null;
    /** One refetch of the PDF link per review entry when the viewer reports a failed load. */
    private refetchedLink = false;
    Trail: readonly AGUCrumbItem[] = [];

    /** The view the location asked for; `view` is what the CV state allows of it. */
    private requested: CareerExplorerView = 'start';
    private previousModuleKey: string = RA_MODULE_KEYS.CareerMap;
    private locationSub: Subscription | null = null;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollGeneration = 0;
    private reviewTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly pendingReview = new Map<ResearchCvReviewStatus, Set<string>>();
    private uploadedFileName: string | null = null;
    private pollFailures = 0;
    /** Upload time + `CV_SUBMIT_GRACE_MS`: until then a `not_submitted` CV is polled with the reading steps (H-2 d); null otherwise. */
    private submitGraceUntil: number | null = null;
    /** The CV on screen is `not_submitted` inside the grace window: it reads as step 0, not as the not-processed copy. */
    private awaitingSubmit = false;
    private menuFocusTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private readonly api: ResearchApiService,
        private readonly portalConfig: PortalConfigService,
        private readonly host: ExplorerRaHostAdapter,
        private readonly render: AGURenderService,
        private readonly confirm: MJConfirmService,
        @Optional() @Inject(AGUCrumbService) private readonly crumbs: AGUCrumbService | null = null,
        @Optional() private readonly element: ElementRef<HTMLElement> | null = null,
        /** The surface's membership state; absent outside a surface, where nothing is locked. */
        @Optional() @Inject(MembershipService) private readonly membership: MembershipService | null = null
    ) {}

    /** The lapsed-membership lock that replaces the page body (P/proto-states.js:137). */
    readonly LockCopy: StatePanelCopy = CAREER_LOCK_COPY;
    /** True while the token says the membership has lapsed; `unknown` (today's token) is not lapsed. */
    get Lapsed(): boolean { return this.membership?.IsLapsed ?? false; }
    /** Both lock buttons go to the AGU account page. */
    OnRenew(): void { void this.membership?.OpenRenewal(this.portalConfig); }

    // ── Lifecycle ────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        void this.Open();
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
        this.StopPolling();
        if (this.reviewTimer !== null) clearTimeout(this.reviewTimer);
        if (this.menuFocusTimer !== null) clearTimeout(this.menuFocusTimer);
        this.menuFocusTimer = null;
        this.crumbs?.Publish([]);
    }

    /**
     * The surface keeps visited modules mounted, so a return to Career Explorer is a re-entry: polling stops on leave and the CV
     * and matches are asked again on return (`CareerMatches(false)` is re-asked every entry, so parse-only support lifts without a
     * deploy; quietly when matches for the same CV are already on screen). Moving between this module's views is not re-entry.
     */
    OnLocationChanged(loc: AGUAppLocation): void {
        const entering = IsModuleReentry(RA_MODULE_KEYS.CareerMap, this.previousModuleKey, loc.ModuleKey);
        const leaving = loc.ModuleKey !== RA_MODULE_KEYS.CareerMap && this.previousModuleKey === RA_MODULE_KEYS.CareerMap;
        this.previousModuleKey = loc.ModuleKey;
        if (leaving) {
            this.StopPolling();
            return;
        }
        if (loc.ModuleKey !== RA_MODULE_KEYS.CareerMap) return;
        this.category = loc.View === 'review' ? loc.RecordID ?? null : null;
        this.roleRef = loc.View === 'role' ? loc.RecordID ?? null : null;
        this.requested = IsCareerExplorerView(loc.View) ? loc.View : 'start';
        this.SyncView();
        if (entering) void this.Open();
        this.render.Notify();
    }

    /** Loads the CV; for a finished CV, its matches (and, with full provider support, the job profiles); for a parsing CV, starts polling. */
    async Open(): Promise<void> {
        this.StopPolling();
        this.pollFailures = 0;
        this.pollTroubled = false;
        this.loadError = null;
        this.profilesFailed.clear();
        try {
            const [cv, config] = await Promise.all([this.api.MyCv(), this.portalConfig.Load().catch(() => null)]);
            this.config = config ?? this.config;
            this.ApplyCv(cv);
            this.loading = false;
            this.render.Notify();
            await this.ContinueFromCv();
        } catch (e) {
            this.loadError = DescribeError(e);
        } finally {
            this.loading = false;
            this.render.Notify();
        }
    }

    Retry(): void {
        void this.Open();
    }

    // ── CV state ─────────────────────────────────────────────────────────

    private ApplyCv(cv: ResearchMemberCv | null): void {
        const previous = this.cv;
        // Same document, parsing again after it was done (or still re-reading): the server's own report of a re-read.
        this.rereading = cv !== null && IsParsing(cv) && previous !== null && previous.documentID === cv.documentID && (previous.parseState === 'done' || this.rereading);
        this.cv = cv;
        this.awaitingSubmit = cv !== null && AwaitingSubmit(cv, this.submitGraceUntil, Date.now());
        if (cv && cv.parseState !== 'not_submitted') this.submitGraceUntil = null;
        if (!cv) this.matches = null;
        else this.uploadedFileName = null;
        this.SyncView();
    }

    /** What to do next for the CV on screen: poll a parsing one, load matches for a done one, nothing for the rest. */
    private async ContinueFromCv(): Promise<void> {
        const cv = this.cv;
        if (!cv) return;
        if (IsParsing(cv) || this.awaitingSubmit) {
            this.SchedulePoll();
            return;
        }
        // Matches already on screen for this CV refresh quietly (a return to a role must not flash the reading steps, H-13).
        if (cv.parseState === 'done') await this.LoadMatches(false, this.matches !== null && this.matchesFor === cv.documentID);
    }

    /**
     * `quiet` skips the reading steps: a refresh re-scores behind the page the member is already looking at. Only the latest request's
     * answer is applied; an older one landing after it is dropped (I-2).
     */
    private async LoadMatches(refresh: boolean, quiet = false): Promise<void> {
        const request = ++this.matchesRequest;
        const documentID = this.cv?.documentID ?? null;
        this.matchesInFlight = !quiet;
        this.matchesError = null;
        this.render.Notify();
        try {
            const matches = await this.api.CareerMatches(refresh);
            if (request !== this.matchesRequest) return;
            this.matches = matches;
            this.matchesFor = documentID;
            this.reread = matches.rereadDroppedDecisions > 0 ? { dropped: matches.rereadDroppedDecisions } : null;
            this.matchesInFlight = false;
            this.SyncView();
            // Parse-only: no role cards, so there are no profiles to read. The fail-silent path: a failed profile read leaves the cards
            // without salary/education tiles, never an error banner (I-3).
            if (matches.providerSupport === 'full') await this.EnsureProfiles(TopRoles(matches, MAX_PROFILE_ROLES).map((m) => m.roleRef));
        } catch (e) {
            if (request === this.matchesRequest) this.matchesError = DescribeError(e);
        } finally {
            if (request === this.matchesRequest) this.matchesInFlight = false;
            this.render.Notify();
        }
    }

    // ── Paths and role data ──────────────────────────────────────────────

    /**
     * What the view on screen still needs: the paths view every role's profile (the first 20 came with the matches), the role view its
     * profile, narrative and openings. Never on parse-only: Julius is unshipped there, so `CareerNarrative` and `CareerJobProfiles`
     * are not called. Each piece asks once per key, so the repeated calls (every sync, every load) are cheap.
     */
    private EnsureViewData(): void {
        if (this.parseOnly || !this.matches) return;
        if (this.view === 'paths') {
            void this.EnsureProfiles(this.matches.matches.map((m) => m.roleRef));
        } else if (this.view === 'role') {
            void this.EnsureRoleData();
        } else {
            this.narrativeFailedFor = null;
            // A failed listing read is retried on the next entry to a role, not on every sync.
            if (this.openingsState === 'failed') { this.openingsFor = null; this.openingsState = 'idle'; }
        }
    }

    /**
     * Reads the profiles not held yet, 20 refs a call (the server's cap). Every ref is marked in flight up front so a repeated call (a
     * sync, a landed chunk) never asks twice. A chunk that lands re-runs `EnsureViewData`: a role waiting on its profile continues. A
     * failure leaves the cards without postings and is remembered, so nothing loops.
     */
    private async EnsureProfiles(roleRefs: readonly string[]): Promise<void> {
        const missing = roleRefs.filter((r) => !this.profiles.has(r) && !this.profilesInFlight.has(r) && !this.profilesFailed.has(r));
        for (const r of missing) this.profilesInFlight.add(r);
        for (let i = 0; i < missing.length; i += MAX_PROFILE_ROLES) {
            const chunk = missing.slice(i, i + MAX_PROFILE_ROLES);
            try {
                const profiles = await this.api.CareerJobProfiles(chunk);
                const next = new Map(this.profiles);
                for (const p of profiles) next.set(p.roleRef, p);
                this.profiles = next;
                // A role the provider answered without a profile is settled, not pending: record it with the failures so the
                // next pass (EnsureViewData below) does not ask for it again — otherwise each answer re-triggers the same request.
                for (const r of chunk) if (!next.has(r)) this.profilesFailed.add(r);
            } catch {
                for (const r of chunk) this.profilesFailed.add(r);
            } finally {
                for (const r of chunk) this.profilesInFlight.delete(r);
            }
            this.render.Notify();
            this.EnsureViewData();
        }
    }

    private async EnsureRoleData(): Promise<void> {
        const roleRef = this.roleRef;
        const match = this.CurrentRole;
        if (roleRef === null || match === null) return;
        void this.EnsureNarrative(roleRef, match);
        await this.EnsureProfiles([roleRef]);
        await this.EnsureOpenings(roleRef);
    }

    private async EnsureNarrative(roleRef: string, match: ResearchRoleMatch): Promise<void> {
        const key = `${roleRef}|${match.narrativeKey}`;
        if (this.narrativeFor === key || this.narrativeFailedFor === key || this.narrativeLoading) return;
        this.narrative = null;
        this.narrativeLoading = true;
        this.render.Notify();
        try {
            const narrative = await this.api.CareerNarrative(roleRef);
            if (this.CurrentNarrativeKey === key) {
                this.narrative = narrative;
                this.narrativeFor = key;
            }
        } catch {
            // No error panel (brief): the role view shows the rest without a narrative, and does not ask again until the member re-enters.
            this.narrativeFailedFor = key;
        } finally {
            this.narrativeLoading = false;
            this.render.Notify();
        }
        // The role changed while this was in flight: the new one has not asked yet.
        if (this.CurrentNarrativeKey !== key) this.EnsureViewData();
    }

    private get CurrentNarrativeKey(): string | null {
        const match = this.CurrentRole;
        return this.view === 'role' && match !== null ? `${match.roleRef}|${match.narrativeKey}` : null;
    }

    /**
     * The role's listings. Waiting on the role's profile is `idle` (the view shows the skeleton); `failed` is only ever a real failure:
     * a `SearchJobs` that threw, or a profile read that did.
     */
    private async EnsureOpenings(roleRef: string): Promise<void> {
        if (this.openingsFor === roleRef) return;
        const profile = this.profiles.get(roleRef);
        if (!profile) {
            if (this.profilesFailed.has(roleRef)) {
                this.openingsFor = roleRef;
                this.openings = null;
                this.openingsState = 'failed';
                this.render.Notify();
            }
            return;
        }
        this.openingsFor = roleRef;
        const title = profile.searchTitles[0]?.title ?? null;
        if (title === null) {
            this.openings = [];
            this.openingsState = 'done';
            this.render.Notify();
            return;
        }
        this.openings = null;
        this.openingsState = 'loading';
        this.render.Notify();
        try {
            const result = await this.api.SearchJobs({ query: { text: title }, count: OPENINGS_COUNT, page: 1, sort: 'best', understand: false });
            if (this.openingsFor === roleRef) {
                this.openings = result.results;
                this.openingsState = 'done';
            }
        } catch {
            // The role view says the listings could not be loaded; it never claims there are none.
            if (this.openingsFor === roleRef) {
                this.openings = null;
                this.openingsState = 'failed';
            }
        } finally {
            this.render.Notify();
        }
    }

    /** The role on screen, from the matches; null for an unknown ref or before the matches load. */
    get CurrentRole(): ResearchRoleMatch | null {
        const ref = this.roleRef;
        return ref === null ? null : this.matches?.matches.find((m) => m.roleRef === ref) ?? null;
    }

    get CurrentProfile(): ResearchJobProfile | null {
        return this.roleRef === null ? null : this.profiles.get(this.roleRef) ?? null;
    }

    /** The narrative only while it is this role's (a role change must not flash the last one). */
    get RoleNarrative(): ResearchCareerNarrative | null {
        return this.narrative !== null && this.narrative.roleRef === this.roleRef ? this.narrative : null;
    }

    get RoleOpenings(): ResearchJobMatch[] | null {
        return this.openingsFor === this.roleRef ? this.openings : null;
    }

    get RoleOpeningsState(): OpeningsState {
        return this.openingsFor === this.roleRef ? this.openingsState : 'idle';
    }

    /** "Based on this place": the role view's own location, the origin a job or the Job Finder carries so its crumb comes back here. */
    private get RoleLocation(): AGUAppLocation {
        return this.host.CurrentLocation ?? { ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'role', RecordID: this.roleRef ?? undefined };
    }

    /** An opening: the job's detail, opened from this role (Task 9 `From`). */
    OnOpenJob(jobID: string): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'detail', RecordID: jobID, From: this.RoleLocation });
    }

    /** The empty copy's "Browse all jobs" (P6:1702): the plain finder, with no query and no origin, so it is never cx mode. */
    OnBrowseAllJobs(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search' });
    }

    /**
     * "See all jobs for this path in Job Finder": the finder opens searching the role's first search title (`Query`, Task 10) with this
     * role as its origin, which puts it in cx mode. A role with no search title has no query to hand over: the plain finder.
     */
    OnSeeAllJobs(): void {
        const title = this.CurrentProfile?.searchTitles[0]?.title ?? null;
        if (title === null) {
            this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search' });
            return;
        }
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'search', Query: title, From: this.RoleLocation });
    }

    /** Which view the CV allows: no finished CV is `start`; parse-only matches pull `paths`/`role` back to `ready`. */
    private ViewFor(): CareerExplorerView {
        const requested = this.requested;
        const cv = this.cv;
        if (!cv || cv.parseState !== 'done' || this.replacing) return 'start';
        if (requested === 'start') return 'ready';
        // Review quotes the real PDF; any other format stays on the saved-for-matching copy (brief: non-PDF never reaches review).
        if (requested === 'review' && cv.detectedType !== 'pdf') return 'ready';
        if (this.parseOnly && (requested === 'paths' || requested === 'role')) return 'ready';
        return requested;
    }

    private SyncView(): void {
        this.view = this.ViewFor();
        // Parse-only has no paths or role view: replace the location with ready (no extra history entry) so the URL says what is shown.
        if (this.view === 'ready' && this.parseOnly && (this.requested === 'paths' || this.requested === 'role')) {
            this.requested = 'ready';
            this.host.ResetTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'ready' });
        }
        this.PublishTrail();
        // Review quotes the PDF; the ready view's "Your CV" card shows its first page (a PDF only).
        if (this.view === 'review' || (this.view === 'ready' && this.cv?.detectedType === 'pdf')) {
            void this.EnsureDownloadUrl();
        } else {
            // The link is short-lived: leaving review and ready drops it, the next entry reads a fresh one.
            this.downloadFor = null;
            this.downloadUrl = null;
            this.refetchedLink = false;
        }
        this.EnsureViewData();
    }

    // ── Polling ──────────────────────────────────────────────────────────

    private get PollIntervalMs(): number {
        return this.config?.cvPollIntervalMs ?? DEFAULT_CV_POLL_MS;
    }

    /** One chained `setTimeout` at a time; a generation counter makes a tick that was already in flight inert after a stop. */
    private SchedulePoll(): void {
        this.StopPolling();
        const generation = ++this.pollGeneration;
        this.pollTimer = setTimeout(() => void this.PollTick(generation), this.PollIntervalMs);
    }

    private async PollTick(generation: number): Promise<void> {
        this.pollTimer = null;
        try {
            const cv = await this.api.MyCv();
            if (generation !== this.pollGeneration) return;
            this.pollFailures = 0;
            this.pollTroubled = false;
            this.ApplyCv(cv);
            this.render.Notify();
            await this.ContinueFromCv();
        } catch {
            if (generation !== this.pollGeneration) return;
            // A failed poll is not a failed CV: keep asking on the same interval, and say so after three misses in a row.
            this.pollFailures++;
            if (this.pollFailures >= POLL_FAILURES_BEFORE_BANNER) this.pollTroubled = true;
            this.SchedulePoll();
        }
        this.render.Notify();
    }

    private StopPolling(): void {
        this.pollGeneration++;
        if (this.pollTimer !== null) clearTimeout(this.pollTimer);
        this.pollTimer = null;
    }

    // ── Upload, replace, delete ──────────────────────────────────────────

    /** Sends the CV. `consent` must be the member's own tick: nothing is sent, and no request is made, without it. */
    async Upload(file: File, consent: boolean): Promise<void> {
        this.uploadError = null;
        if (!consent) {
            this.uploadError = CONSENT_REQUIRED_COPY;
            this.render.Notify();
            return;
        }
        this.uploading = true;
        this.uploadedFileName = file.name;
        this.render.Notify();
        let uploaded = false;
        try {
            await this.api.UploadDocument(file, 'cv', true);
            uploaded = true;
            this.submitGraceUntil = Date.now() + CV_SUBMIT_GRACE_MS;
            this.matches = null;
            this.matchesFor = null;
            this.reread = null;
            const cv = await this.api.MyCv();
            // `replacing` holds until the new CV is here: while MyCv runs, `cv` is still the old done one, and dropping it earlier
            // showed the upload drop for 100-200 ms between finalize and this read (H-2 d, H-7).
            this.replacing = false;
            this.ApplyCv(cv);
            await this.ContinueFromCv();
        } catch (e) {
            if (uploaded) this.replacing = false;
            this.uploadError = DescribeError(e);
        } finally {
            this.uploading = false;
            this.render.Notify();
        }
    }

    OnFileChosen(file: File): void {
        void this.Upload(file, this.consent);
    }

    OnRefused(message: string): void {
        this.uploadError = message;
        this.render.Notify();
    }

    OnConsentChange(consent: boolean): void {
        this.consent = consent;
        if (consent && this.uploadError === CONSENT_REQUIRED_COPY) this.uploadError = null;
        this.render.Notify();
    }

    /** Replace / "Choose another file": the start view with the picker opened for the member (P6:1827). A CV already consented to needs no second tick. */
    Replace(): void {
        this.StopPolling();
        this.replacing = true;
        // The grace belonged to the file just sent; the member is choosing another, so the last one reads as it is (Task 29c).
        this.submitGraceUntil = null;
        this.awaitingSubmit = false;
        this.uploadError = null;
        if (this.cv?.consentedAt) this.consent = true;
        this.SyncView();
        this.render.Notify();
    }

    /** The way back from Replace to the CV on file. */
    KeepCurrent(): void {
        this.replacing = false;
        this.uploadError = null;
        this.SyncView();
        this.render.Notify();
        void this.ContinueFromCv();
    }

    get CanKeepCurrent(): boolean {
        return this.replacing && this.cv?.parseState === 'done';
    }

    async Delete(): Promise<void> {
        const cv = this.cv;
        if (!cv) return;
        const yes = await this.AskConfirm();
        if (!yes) return;
        this.StopPolling();
        try {
            await this.api.DocumentDelete(cv.documentID);
            this.cv = null;
            this.submitGraceUntil = null;
            this.awaitingSubmit = false;
            this.matches = null;
            this.matchesFor = null;
            this.profiles = new Map();
            this.narrative = null;
            this.narrativeFor = null;
            this.openings = null;
            this.openingsFor = null;
            this.openingsState = 'idle';
            this.reread = null;
            this.replacing = false;
            this.consent = false; // P6:1829 `S.cons=false`
            this.SyncView();
        } catch (e) {
            this.uploadError = DescribeError(e);
        }
        this.render.Notify();
    }

    private async AskConfirm(): Promise<boolean> {
        return this.confirm.ConfirmDelete({
            title: 'Delete your CV?',
            message: 'Your CV, its tags and your career paths are removed. You can upload another file at any time.',
            confirmText: 'Delete CV',
            cancelText: 'Keep it',
        });
    }

    // ── Review actions ───────────────────────────────────────────────────

    /** Queues review decisions; they settle for 600 ms, then go as one `ReviewTags` per status and the matches are re-read. */
    QueueReview(providerTagIDs: readonly string[], status: ResearchCvReviewStatus): void {
        const ids = this.pendingReview.get(status) ?? new Set<string>();
        for (const id of providerTagIDs) ids.add(id);
        this.pendingReview.set(status, ids);
        if (this.reviewTimer !== null) clearTimeout(this.reviewTimer);
        this.reviewTimer = setTimeout(() => void this.FlushReview(), REVIEW_DEBOUNCE_MS);
    }

    private async FlushReview(): Promise<void> {
        this.reviewTimer = null;
        const batches = [...this.pendingReview.entries()];
        this.pendingReview.clear();
        try {
            for (const [status, ids] of batches) await this.api.ReviewTags([...ids], status);
            // The cards come from the CV's tags: re-read them before the optimistic decisions are dropped.
            const cv = await this.api.MyCv();
            if (cv) this.cv = cv;
            this.ForgetDecisions(batches);
            this.render.Notify();
            await this.LoadMatches(false, true);
        } catch (e) {
            this.ForgetDecisions(batches);
            this.matchesError = DescribeError(e);
            this.render.Notify();
        }
    }

    // ── Review view ──────────────────────────────────────────────────────

    private ForgetDecisions(batches: ReadonlyArray<readonly [ResearchCvReviewStatus, Set<string>]>): void {
        for (const [, ids] of batches) for (const id of ids) this.decisions.delete(id);
        this.decisionsVersion += 1;
    }

    /** The cards and tab counts, recomputed only when the CV or a decision changes (the template reads them every pass). */
    private get ReviewMemo(): { cards: ReviewCard[]; pending: Record<ReviewCategory, number> } {
        const cv = this.cv;
        if (!cv) return EMPTY_REVIEW;
        if (this.cardsMemo === null || this.cardsMemo.cv !== cv || this.cardsMemo.version !== this.decisionsVersion) {
            const cards = GroupReviewTags(cv.tags, cv.segments).map((c) => {
                const status = this.decisions.get(c.providerTagID);
                return status ? { ...c, reviewStatus: status } : c;
            });
            this.cardsMemo = { cv, version: this.decisionsVersion, cards, pending: PendingCounts(cards) };
        }
        return this.cardsMemo;
    }

    /** The review cards, with decisions still in flight already applied. */
    get ReviewCards(): ReviewCard[] { return this.ReviewMemo.cards; }

    get ReviewStatedCount(): number { return this.cv ? StatedCount(this.cv.tags) : 0; }
    get ReviewPending(): Readonly<Record<ReviewCategory, number>> { return this.ReviewMemo.pending; }

    /** The category in view: the location's, else the first with tags still pending, else the first. */
    get ReviewCategoryNow(): ReviewCategory {
        const asked = ReviewCategories.find((c) => c === this.category);
        if (asked) return asked;
        const pending = this.ReviewPending;
        return ReviewCategories.find((c) => pending[c] > 0) ?? ReviewCategories[0];
    }

    /** Accept / Decline from the review view: the card leaves at once, the server hears it after the debounce. */
    OnReviewDecision(ids: readonly string[], status: ResearchCvReviewStatus): void {
        for (const id of ids) this.decisions.set(id, status);
        this.decisionsVersion += 1;
        this.QueueReview(ids, status);
        this.render.Notify();
    }

    /** The member adds a vocabulary tag as their own (Sharpen your results): asserted, then the CV's tags and the scores are re-read. */
    OnAssert(providerTagID: string): void {
        void this.SetAsserted(providerTagID, true);
    }

    OnRetract(providerTagID: string): void {
        void this.SetAsserted(providerTagID, false);
    }

    /** The last add's result, handed to the ready view (a new object each time) so it can keep or clear the typed name. */
    assertOutcome: { ok: boolean } | null = null;

    /** A failed Add is told inline by the ready view (`assertOutcome`), so it raises no page banner too (Task 25); any other failure does. */
    private async SetAsserted(providerTagID: string, asserted: boolean): Promise<void> {
        let sent = false;
        try {
            await this.api.AssertTag(providerTagID, asserted);
            sent = true;
            if (asserted) this.assertOutcome = { ok: true };
            const cv = await this.api.MyCv();
            if (cv) this.cv = cv;
            this.render.Notify();
            await this.LoadMatches(false, true);
        } catch (e) {
            if (asserted && !sent) this.assertOutcome = { ok: false };
            else this.matchesError = DescribeError(e);
            this.render.Notify();
        }
    }

    OnAcceptAll(): void {
        const ids = this.ReviewCards.filter((c) => c.category === this.ReviewCategoryNow && c.reviewStatus === 'unreviewed').map((c) => c.providerTagID);
        if (ids.length > 0) this.OnReviewDecision(ids, 'approved');
    }

    OnReviewNext(category: ReviewCategory): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'review', RecordID: category });
    }

    OpenPaths(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'paths' });
    }

    /** The note's own dismissal is persisted by the review view; the page only repaints. */
    OnDismissNote(): void { this.render.Notify(); }

    /** The viewer could not load the PDF (an expired link, say): read a fresh link, once per review entry. */
    OnViewerFailed(): void {
        if (this.refetchedLink) return;
        this.refetchedLink = true;
        this.downloadFor = null;
        void this.EnsureDownloadUrl();
    }

    private async EnsureDownloadUrl(): Promise<void> {
        const cv = this.cv;
        if (!cv || this.downloadFor === cv.documentID) return;
        this.downloadFor = cv.documentID;
        try {
            this.downloadUrl = await this.api.DocumentDownloadUrl(cv.documentID);
        } catch {
            // The viewer shows its own "could not show your document"; the tags stay reviewable. Retry on the next entry.
            this.downloadFor = null;
            this.downloadUrl = null;
        }
        this.render.Notify();
    }

    // ── Navigation ───────────────────────────────────────────────────────

    OpenReview(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'review' });
    }

    OpenProfile(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    Navigate(location: AGUAppLocation): void {
        this.host.NavigateTo(location);
    }

    private PublishTrail(): void {
        const role = this.matches?.matches.find((m) => m.roleRef === this.roleRef) ?? null;
        this.Trail = TrailFor(this.view, role?.title ?? null, this.category);
        this.crumbs?.Publish(this.Trail);
    }

    // ── Chrome ───────────────────────────────────────────────────────────

    get PrivacyUrl(): string | null { return this.config?.careerExplorerPrivacyUrl || null; }
    get TermsUrl(): string | null { return this.config?.careerExplorerTermsUrl || null; }
    get ContactEmail(): string | null { return this.config?.careerExplorerContactEmail || null; }
    get ContactHref(): string | null { return this.ContactEmail ? `mailto:${this.ContactEmail}?subject=Career%20Explorer` : null; }

    /** A click outside the kebab closes its menu (P6:1779-1780). */
    @HostListener('document:click', ['$event'])
    OnDocumentClick(event: Event): void {
        const target = event.target;
        if (this.MenuOpen && target instanceof Element && !target.closest('.cx-mm')) this.CloseMenu();
    }

    /** Esc closes the menu and returns focus to its button (P6:1779 closes; the menu role needs the focus return). */
    @HostListener('keydown.escape')
    OnEscape(): void {
        if (!this.MenuOpen) return;
        this.CloseMenu();
        this.FocusMenuButton();
    }

    /** ArrowUp/ArrowDown move through the menu items (`role="menu"`). */
    OnMenuKeydown(event: KeyboardEvent): void {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        const items = this.MenuItems();
        if (items.length === 0) return;
        const at = items.indexOf(document.activeElement as HTMLElement);
        const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
        items[next]?.focus();
    }

    /** Opens focus on the first item (P6:1821 `f.focus()`). */
    ToggleMenu(): void {
        this.MenuOpen = !this.MenuOpen;
        if (this.MenuOpen) {
            this.render.Notify();
            if (this.menuFocusTimer !== null) clearTimeout(this.menuFocusTimer);
            this.menuFocusTimer = setTimeout(() => {
                this.menuFocusTimer = null;
                this.MenuItems()[0]?.focus();
            }, 0);
        }
    }

    CloseMenu(): void { this.MenuOpen = false; }

    /** The dialog returns focus to whatever had it when it opened, so a menu item (about to leave the DOM) hands focus back to the kebab first. */
    OpenAbout(): void {
        if (this.MenuOpen) this.FocusMenuButton();
        this.MenuOpen = false;
        this.AboutOpen = true;
    }

    private MenuItems(): HTMLElement[] {
        const found = this.element?.nativeElement.querySelectorAll<HTMLElement>('.cx-mmp [role="menuitem"]');
        return found ? Array.from(found) : [];
    }

    private FocusMenuButton(): void {
        this.element?.nativeElement.querySelector<HTMLElement>('.cx-mmb')?.focus();
    }
    CloseAbout(): void { this.AboutOpen = false; this.render.Notify(); }

    // ── Template state ───────────────────────────────────────────────────

    /** `matches.providerSupport === 'parse_only'`: the provider reads CVs but cannot match roles yet. A calm state, never an error. */
    get parseOnly(): boolean {
        return this.matches?.providerSupport === 'parse_only';
    }

    /** The parse-only panel: the on-their-way copy, or the calm saved line when CV reading is switched off (`rereadBlocked`). */
    get ParseOnlyPanel(): ParseOnlyMessage {
        return ParseOnlyCopy(this.matches?.rereadBlocked === true, this.cv?.tags.length ?? 0);
    }

    /** The reading step to show, 0..2, or null. */
    get ReadingStepIndex(): 0 | 1 | 2 | null {
        if (!this.cv) return null;
        return this.awaitingSubmit ? 0 : ReadingStep(this.cv, this.matchesInFlight);
    }

    get ReadingHeading(): string {
        if (this.rereading) return 'Re-reading your CV';
        // While a replacement uploads, `cv` is still the old file: name the one being sent.
        const name = (this.uploading ? this.uploadedFileName : null) ?? this.cv?.fileName ?? this.uploadedFileName ?? 'your CV';
        return `Reading ${name}`;
    }

    get NotProcessed(): NotProcessedMessage | null {
        return this.cv && !this.awaitingSubmit ? NotProcessedCopy(this.cv) : null;
    }

    get RereadBanner(): string | null {
        if (!this.reread) return null;
        const n = this.reread.dropped;
        // Spec §F3: "Your CV was read again; {n} earlier answers no longer apply".
        return `Your CV was read again; ${n} earlier ${n === 1 ? 'answer no longer applies' : 'answers no longer apply'}`;
    }

    /** What the start view shows. A file just sent shows the reading steps before the first poll lands. */
    get StartMode(): CxStartMode {
        const cv = this.cv;
        if (this.replacing || !cv) return this.uploading && this.uploadedFileName !== null ? 'reading' : 'upload';
        if (this.ReadingStepIndex !== null) return 'reading';
        if (this.NotProcessed) return 'not-processed';
        if (cv.parseState === 'failed') return 'failed';
        return 'upload';
    }

    get StartStep(): 0 | 1 | 2 | null {
        return this.ReadingStepIndex ?? (this.StartMode === 'reading' ? 0 : null);
    }

    /** The one polite live region's text: the reading heading and step, or the not-processed title; empty otherwise. */
    get StatusText(): string {
        if (this.StartMode === 'reading') {
            const step = this.StartStep;
            return step === null ? this.ReadingHeading : `${this.ReadingHeading}. ${READING_STEPS[step]}`;
        }
        return this.StartMode === 'not-processed' ? this.NotProcessed?.Title ?? '' : '';
    }
}
